import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, KeyRound, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BRAND_COLORS } from "@/lib/menuData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";

export default function EmployeeLogin() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!pin.trim()) {
      setError("Please enter your PIN");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API}/employee/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pin.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        // Store employee session
        sessionStorage.setItem("employeeSession", JSON.stringify({
          authenticated: true,
          name: data.employee_name || "Employee",
          loginTime: Date.now(),
        }));
        toast.success(`Welcome, ${data.employee_name || "Employee"}!`);
        navigate("/hub?mode=instore");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Invalid PIN");
      }
    } catch (err) {
      setError("Failed to login. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(value);
    setError("");
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: BRAND_COLORS.sandWhite }}
    >
      <div className="w-full max-w-sm">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Logo" 
            className="w-20 h-20 mx-auto rounded-2xl shadow-lg mb-4"
          />
          <h1 
            className="text-2xl font-bold mb-1"
            style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}
          >
            Island Chevron & Grill
          </h1>
          <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
            Employee Order System
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl shadow-xl p-6 border border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "#0066cc" }}
            >
              <Store className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ color: BRAND_COLORS.darkSlate }}>
                In-Store Mode
              </h2>
              <p className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                Enter your employee PIN to continue
              </p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: BRAND_COLORS.darkSlate }}>
                Employee PIN
              </label>
              <div className="relative">
                <KeyRound 
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" 
                  style={{ color: BRAND_COLORS.darkSlate + "66" }} 
                />
                <Input
                  data-testid="employee-pin-input"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Enter PIN"
                  value={pin}
                  onChange={handlePinChange}
                  className="h-14 pl-11 text-lg tracking-widest rounded-xl border-slate-200 focus:border-blue-500"
                  autoComplete="off"
                />
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-500">{error}</p>
              )}
            </div>

            <Button
              data-testid="employee-login-btn"
              type="submit"
              className="w-full h-14 rounded-xl font-bold text-base text-white"
              style={{ backgroundColor: "#0066cc" }}
              disabled={loading || !pin.trim()}
            >
              {loading ? (
                "Logging in..."
              ) : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Login
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-center mt-4" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
            Contact manager if you need a PIN
          </p>
        </div>

        {/* Back to Customer Mode */}
        <button
          className="w-full mt-4 text-sm font-medium py-3 rounded-xl transition-colors"
          style={{ color: BRAND_COLORS.darkSlate + "99" }}
          onClick={() => navigate("/hub")}
        >
          ← Back to Customer Mode
        </button>
      </div>
    </div>
  );
}
